import { CountrySelectorFormsy } from 'app/components/CountrySelectorFormsy'
import { TextInput } from 'app/components/FormsyHelperComponent'
import PhoneNumberInputFormsy, {
  validatePhoneNumber,
} from 'app/components/PhoneNumberInputFormsy'
import { Typography } from '@material-ui/core'
import { withoutPostcodeCountries } from 'libs/country'
import StyledStateSelectorFormsy from 'app/components/StyledStateSelectorFormsy'
import { getFullPhoneNumber, useCountryCode } from 'commons/phoneNumber'
import { parsedPhoneNumber } from 'commons/phoneNumber'
import { isEmptyOrNil } from 'libs/empty'
import * as R from 'ramda'
import React from 'react'
import { useIntl } from 'react-intl'
import PropTypes from 'prop-types'

import { RowInput, RowInputTwoColumns } from './PlanBillingFormHelper'

export const parsePhoneNumber = (value) =>
  R.defaultTo(
    { phoneNumber: undefined },
    parsedPhoneNumber(R.propOr('', 'phoneNumber', value)),
  )

PlanBillingDetailsForm.propTypes = {
  initialValue: PropTypes.object,
  formRef: PropTypes.object.isRequired,
}

export function PlanBillingDetailsForm(props) {
  const {
    initialValue = {},
    formRef,
    displayTitle = true,
    className,
    required = true,
  } = props
  const { formatMessage } = useIntl()
  const { countryCode } = useCountryCode()
  const defaultCountry = isEmptyOrNil(R.prop('countryCode', initialValue))
    ? countryCode
    : R.prop('countryCode', initialValue)

  return (
    <div className={className}>
      {displayTitle && (
        <Typography>
          {formatMessage({
            id: 'planAndBilling.billingDetails.form.subTitle.label',
          })}
        </Typography>
      )}
      <RowInputTwoColumns
        icon="account_circle"
        columnOne={
          <TextInput
            labelId="firstName.label"
            placeholderId="firstName.label"
            name="firstName"
            inputProps={{
              'data-testid': 'firstNameInput',
            }}
            value={R.prop('firstName', initialValue)}
            required={required}
            maxLength={40}
            fullWidth
          />
        }
        columnTwo={
          <TextInput
            labelId="lastName.label"
            placeholderId="lastName.label"
            name="lastName"
            value={R.prop('lastName', initialValue)}
            inputProps={{
              'data-testid': 'lastNameInput',
            }}
            required={required}
            maxLength={40}
            fullWidth
          />
        }
      />
      <RowInput icon="home">
        <TextInput
          containerClassName="w-full"
          labelId="planAndBilling.billingDetails.form.address1.label"
          placeholderId="planAndBilling.billingDetails.form.address1.label"
          name="address1"
          inputProps={{
            'data-testid': 'address1Input',
          }}
          value={R.prop('address1', initialValue)}
          required={required}
          maxLength={250}
          multiline
          fullWidth
        />
      </RowInput>
      <RowInput icon="home">
        <TextInput
          containerClassName="w-full"
          labelId="planAndBilling.billingDetails.form.address2.label"
          placeholderId="planAndBilling.billingDetails.form.address2.label"
          name="address2"
          inputProps={{
            'data-testid': 'address2Input',
          }}
          value={R.prop('address2', initialValue)}
          maxLength={250}
          multiline
          fullWidth
        />
      </RowInput>
      <RowInputTwoColumns
        icon="home"
        columnOne={
          <CountrySelectorFormsy
            label={formatMessage({
              id: 'planAndBilling.billingDetails.form.country.label',
            })}
            name="country"
            value={R.prop('country', initialValue)}
            inputProps={{
              'data-testid': 'countryInput',
            }}
            FormControlProps={{
              required: true,
            }}
            required={required}
          />
        }
        columnTwo={
          <StyledStateSelectorFormsy
            label={formatMessage({
              id: 'planAndBilling.billingDetails.form.state.label',
            })}
            name="state"
            value={R.prop('state', initialValue)}
            inputProps={{
              'data-testid': 'stateInput',
            }}
            FormControlProps={{
              required,
            }}
            required={required}
            country={R.compose(
              R.prop('country'),
              R.ifElse(isEmptyOrNil, R.always({}), (r) => r.getModel()),
            )(R.prop('current', formRef))}
          />
        }
      />
      <RowInputTwoColumns
        icon="home"
        columnOne={
          <TextInput
            labelId="planAndBilling.billingDetails.form.city.label"
            placeholderId="planAndBilling.billingDetails.form.city.label"
            inputProps={{
              'data-testid': 'cityInput',
            }}
            name="city"
            value={R.prop('city', initialValue)}
            maxLength={40}
            fullWidth
          />
        }
        columnTwo={
          <TextInput
            labelId="planAndBilling.billingDetails.form.zipCode.label"
            placeholderId="planAndBilling.billingDetails.form.zipCode.label"
            name="zipCode"
            inputProps={{
              'data-testid': 'zipCodeInput',
            }}
            validations={{
              matchRegexp: /^[_A-z0-9]*((-|\s)*[_A-z0-9])*$/,
            }}
            validationErrors={{
              matchRegexp: formatMessage({
                id: 'planAndBilling.billingDetails.form.zipCode.validation',
              }),
            }}
            value={R.propOr('', 'zipCode', initialValue)}
            required={
              required &&
              R.compose(
                R.not,
                R.includes(R.__, R.pluck('code', withoutPostcodeCountries)),
                R.toUpper,
                R.propOr('', 'country'),
                R.ifElse(isEmptyOrNil, R.always({}), (r) => r.getModel()),
              )(R.prop('current', formRef))
            }
            maxLength={40}
            fullWidth
          />
        }
      />
      <RowInput icon="local_phone">
        <PhoneNumberInputFormsy
          value={R.ifElse(
            isEmptyOrNil,
            R.always(''),
            R.always(
              getFullPhoneNumber(
                R.prop('countryCode', initialValue),
                R.prop('phoneNumber', initialValue),
              ),
            ),
          )(R.prop('phoneNumber', initialValue))}
          name="phoneNumber"
          containerClassName="w-full"
          autoComplete="new-password"
          enableSearchField
          disableSearchIcon
          disableAreaCodes={true}
          label={formatMessage({
            id: 'phone.label',
          })}
          defaultCountry={defaultCountry}
          validations={{
            isPhoneNumber: validatePhoneNumber,
          }}
          validationErrors={{
            isPhoneNumber: formatMessage({
              id: 'memberForm.phoneNumberInvalid',
            }),
          }}
          inputExtraProps={{
            InputProps: {
              'data-testid': 'phoneNumberInput',
              endAdornment: null,
            },
          }}
          isScrollWhenFocus
        />
      </RowInput>
    </div>
  )
}
